/* eslint-disable react/no-unescaped-entities */
import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import axios from "axios";
import { useAuth, formatApiError } from "@/lib/auth";
import { useLang } from "@/lib/i18n";
import LangSwitch from "@/components/LangSwitch";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Eye, EyeOff, ShieldCheck, Check, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function rules(pw) {
  return { length: pw.length >= 10, upper: /[A-Z]/.test(pw), digit: /\d/.test(pw), special: /[^A-Za-z0-9]/.test(pw) };
}

export default function ChangePassword() {
  const { user, loading, refresh } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const [currentPw, setCurrentPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [show, setShow] = useState(false);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (loading) return <div className="min-h-screen grid place-items-center bg-[#FAFAF7] text-[#5C6B5C]">{t.app.common.loading}</div>;
  if (!user) return <Navigate to="/login" replace />;

  const r = rules(newPw);
  const allOk = r.length && r.upper && r.digit && r.special && newPw === confirmPw;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (newPw !== confirmPw) { setError(t.app.change_pw.mismatch); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/auth/change-password`, { current_password: currentPw, new_password: newPw }, { withCredentials: true });
      toast.success(t.app.change_pw.toast_updated);
      await refresh();
      navigate(user.role === "owner" ? "/dashboard/owner" : user.role === "trainer" ? "/dashboard/trainer" : "/dashboard/client", { replace: true });
    } catch (err) {
      setError(formatApiError(err?.response?.data?.detail));
    } finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen bg-[#FAFAF7] flex items-center justify-center px-6 py-10">
      <div className="w-full max-w-md">
        <div className="flex justify-end mb-4"><LangSwitch variant="light" /></div>
        <div className="text-center mb-8">
          <div className="w-14 h-14 rounded-2xl bg-[#1C3A27] grid place-items-center mx-auto"><ShieldCheck size={28} className="text-[#E05A3D]" /></div>
          <h1 className="mt-5 font-display text-3xl font-black text-[#1C3A27]">
            {user.must_change_password ? t.app.change_pw.title_forced : t.app.change_pw.title_normal}
          </h1>
          <p className="mt-2 text-[#5C6B5C] max-w-sm mx-auto">
            {user.must_change_password ? t.app.change_pw.subtitle_forced : t.app.change_pw.subtitle_normal}
          </p>
        </div>

        <form onSubmit={submit} data-testid="change-password-form" className="bg-white rounded-2xl border border-[#E2E4DF] p-7 space-y-5">
          <div>
            <Label>{t.app.change_pw.current}</Label>
            <Input data-testid="cp-current" type={show ? "text" : "password"} required value={currentPw} onChange={(e) => setCurrentPw(e.target.value)} className="mt-2 h-12 bg-white border-[#E2E4DF] rounded-xl focus-visible:ring-[#E05A3D]" />
          </div>
          <div>
            <Label>{t.app.change_pw.new_pw}</Label>
            <div className="relative mt-2">
              <Input data-testid="cp-new" type={show ? "text" : "password"} required value={newPw} onChange={(e) => setNewPw(e.target.value)} className="h-12 bg-white border-[#E2E4DF] rounded-xl focus-visible:ring-[#E05A3D] pe-10" />
              <button type="button" onClick={() => setShow((v) => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#5C6B5C]">
                {show ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
            <ul className="mt-3 grid grid-cols-2 gap-1.5 text-xs">
              <Rule ok={r.length} text={t.app.signup.rule_length} />
              <Rule ok={r.upper} text={t.app.signup.rule_upper} />
              <Rule ok={r.digit} text={t.app.signup.rule_digit} />
              <Rule ok={r.special} text={t.app.signup.rule_special} />
            </ul>
          </div>
          <div>
            <Label>{t.app.change_pw.confirm}</Label>
            <Input data-testid="cp-confirm" type={show ? "text" : "password"} required value={confirmPw} onChange={(e) => setConfirmPw(e.target.value)} className="mt-2 h-12 bg-white border-[#E2E4DF] rounded-xl focus-visible:ring-[#E05A3D]" />
          </div>
          {error && <div data-testid="cp-error" className="rounded-xl border border-red-200 bg-red-50 text-red-700 text-sm p-3">{error}</div>}
          <Button type="submit" data-testid="cp-submit" disabled={!allOk || submitting} className="w-full h-12 rounded-xl bg-[#1C3A27] hover:bg-[#142A1C] text-white font-semibold">
            {submitting ? t.app.change_pw.submitting : t.app.change_pw.submit}
          </Button>
        </form>
      </div>
    </div>
  );
}

function Rule({ ok, text }) {
  return (
    <li className={`flex items-center gap-1.5 ${ok ? "text-emerald-700" : "text-[#5C6B5C]"}`}>
      {ok ? <Check size={13} /> : <X size={13} />}
      {text}
    </li>
  );
}
